import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {
  Button,
  ButtonGroup,
  DataTable,
  DataTableHead,
  DataTableRow,
  DataTableSorter,
  Divider,
  Grid,
  GridCol,
  PageContainer,
  Tooltip,
  spaceHelpers
} from '@flixbus/honeycomb-react';
import {
  Icon,
  IconDelete,
  IconDeleteSolid,
  IconEditSolid,
  IconOfferSolid,
  IconQuestion
} from '@flixbus/honeycomb-icons-react';
import BusPartnersFilter from '@/Pages/Partners/components/BusPartnersFilter/BusPartnersFilter';
import ContentLoader from '../../Common/ContentLoader';
import {isNil} from 'ramda';
import {confirm} from '../../Common/Confirm';
import {setPartner} from '../store/slice';
import {deletePartner} from '../store/thunks';
import PartnerPanel from './PartnerPanel';
import {useToast} from '../../Common/toasts-container';
import fieldInfoContent from '@/Pages/ProductionCost/constants/fieldInfoContent';
import {BusPartnerSource} from '../store/types';
import {toCapitalized} from '@/Pages/Common/Utils';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import {useBusPartners} from '../hooks/useBusPartners';

const PartnersPage = ({setPartner, deletePartner}) => {
  const {busPartners, refetchBusPartners} = useBusPartners();
  const {countries} = useCountries();

  const {2: space2} = spaceHelpers();
  const {addToast} = useToast();
  const [sortedPartnerRows, setSortedPartnerRows] = useState([]);

  const deletePartnerAction = useCallback(
    async partner => {
      if (
        await confirm({
          PopupIcon: IconDelete,
          title: 'Delete this partner?',
          message: `${partner.name}`
        })
      ) {
        deletePartner(partner, addToast);
      }
    },
    [deletePartner, addToast]
  );

  const getCountryNameById = useCallback(
    id => {
      let country = countries?.find(country => country.id === id);
      return !isNil(country) ? country.name : id;
    },
    [countries]
  );

  const sortAlphabetically = useCallback(
    (sorting, propKey) => {
      const sortedItems = [...sortedPartnerRows].sort((partner1, partner2) =>
        partner1[propKey].localeCompare(partner2[propKey])
      );
      setSortedPartnerRows(
        sorting === 'descending' ? sortedItems.reverse() : sortedItems
      );
    },
    [sortedPartnerRows]
  );

  const sortByName = useCallback(
    sorting => sortAlphabetically(sorting, 'name'),
    [sortAlphabetically]
  );

  const sortByCountry = useCallback(
    sorting => sortAlphabetically(sorting, 'country'),
    [sortAlphabetically]
  );

  const sortByStatus = useCallback(
    sorting => sortAlphabetically(sorting, 'status'),
    [sortAlphabetically]
  );

  const sortBySource = useCallback(
    sorting => sortAlphabetically(sorting, 'source'),
    [sortAlphabetically]
  );

  const mobilityPartnerIdHeader = 'partners-table-mobility-id-header';
  const mobilityPartnerIdLabel = 'Mobility Partner ID';

  const headers = useMemo(
    () => (
      <DataTableHead>
        <th
          scope="col"
          aria-label={mobilityPartnerIdLabel}
          key={mobilityPartnerIdHeader}
          id={mobilityPartnerIdHeader}
        >
          <Tooltip
            id="partners-table-mobility-id-header-tooltip"
            size="content-fit"
            position="top"
            openOnHover
            content={fieldInfoContent.mobilityPartnerIdSalesforce}
          >
            <strong>Mobility Partner ID </strong>
            <Icon
              content={mobilityPartnerIdLabel}
              InlineIcon={IconQuestion}
              size={3}
            />
          </Tooltip>
        </th>
        <DataTableSorter
          aria-label="Sort Partners by name"
          onSortingChange={sortByName}
        >
          Partner name
        </DataTableSorter>
        <DataTableSorter
          aria-label="Sort Partners by country"
          onSortingChange={sortByCountry}
        >
          Country
        </DataTableSorter>

        <DataTableSorter
          aria-label="Sort Partners by status"
          onSortingChange={sortByStatus}
        >
          Status
        </DataTableSorter>

        <DataTableSorter
          aria-label="Sort Partners by source"
          onSortingChange={sortBySource}
        >
          Source
        </DataTableSorter>
        <th scope="col"></th>
      </DataTableHead>
    ),
    [sortByName, sortByCountry, sortByStatus, sortBySource]
  );

  const getDeleteIcon = useCallback(
    partner => {
      const button = (
        <Button
          display="square"
          aria-label="Delete Partner"
          appearance="danger"
          onClick={() => deletePartnerAction(partner)}
          disabled={partner.source === BusPartnerSource.Mpp}
        >
          <Icon InlineIcon={IconDeleteSolid} aria-hidden="true" />
        </Button>
      );

      return partner.source === BusPartnerSource.Mpp ? (
        <Tooltip
          id={`delete-partner-${partner.id}-tooltip`}
          position="top"
          content="Bus Partners created based on MPP data cannot be deleted"
          openOnHover
        >
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      );
    },
    [deletePartnerAction]
  );

  useEffect(() => {
    if (!busPartners) return;
    const sortedPartners = [...busPartners]
      .sort((partner1, partner2) => partner1.name.localeCompare(partner2.name))
      .map(partner => ({
        id: partner.id,
        mobilityPartnerId: partner.mobilityPartnerId,
        name: partner.name,
        country: getCountryNameById(partner.countryId),
        status: partner.status,
        source: partner.source,
        actions: (
          <ButtonGroup alignment="end">
            <Button
              display="square"
              aria-label="Edit Partner"
              onClick={() => setPartner(partner)}
            >
              <Icon InlineIcon={IconEditSolid} aria-hidden="true" />
            </Button>
            {getDeleteIcon(partner)}
          </ButtonGroup>
        )
      }));
    setSortedPartnerRows(sortedPartners);
  }, [getDeleteIcon, getCountryNameById, busPartners, setPartner]);

  if (isNil(busPartners)) {
    return <ContentLoader />;
  }

  return (
    <PageContainer>
      <Grid extraClasses={space2.top}>
        <GridCol size={3}>
          <BusPartnersFilter
            countries={countries}
            onSelected={countryIds => refetchBusPartners({countryIds}, true)}
          />
        </GridCol>
      </Grid>

      <Divider extraClasses={'full-width-divider'} />

      <div className={`hcr-has-text-right hcr-space-4-top hcr-space-2-bottom`}>
        <Button onClick={() => setPartner()}>
          <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
          Add partner
        </Button>
      </div>
      <Grid>
        <GridCol size={12}>
          <DataTable headers={headers}>
            {sortedPartnerRows.map(row => (
              <DataTableRow key={row.id}>
                <td>{row.mobilityPartnerId}</td>
                <td>{row.name}</td>
                <td>{row.country}</td>
                <td>{toCapitalized(row.status)}</td>
                <td>{row.source}</td>
                <td>{row.actions}</td>
              </DataTableRow>
            ))}
          </DataTable>
        </GridCol>
      </Grid>
      <PartnerPanel />
    </PageContainer>
  );
};

export default connect(() => ({}), {setPartner, deletePartner})(PartnersPage);
